/**
 * operatorGrouping.js — Logica pura di raggruppamento e filtro degli operatori.
 *
 * Funzioni pure (nessuna dipendenza da Vue/axios/DOM) usate dalla pagina
 * "Operatori sul Campo" (`src/pages/AdminOperators.vue`) per costruire la
 * Vista_Raggruppata per squadra e per il flusso "Modifica squadra".
 *
 * Sorgenti dati (già filtrate per azienda a monte — VN4/R7):
 *   - operators: da `GET /operators`
 *       { id, nome, cognome, codice_fiscale, telefono, email, qualifica,
 *         costo_orario, is_attivo, is_autonomo, fk_company, team_name, team_id }
 *   - teams: utenti-squadra da `GET /get-users` (filtrati con isSquad + fk_company)
 *       { id, fullname, email, group, fk_company, company_name }
 *
 * Esportate con `module.exports` per compatibilità con l'ambiente Jest
 * CommonJS usato dalle suite esistenti in `GUI/roadme-gui/tests`.
 *
 * Forma del gruppo prodotto da `groupOperatorsByTeam`:
 *   {
 *     team: { id, fullname, group, email, is_autonomo } | null,
 *     operators: [ ...operatori membri... ],
 *     isUnassigned: boolean,   // true solo per il Gruppo_Senza_Squadra
 *     isAutonomous: boolean    // true se almeno un membro è autonomo
 *   }
 */

/**
 * normalizeId — normalizza un identificativo a numero per confronti robusti
 * tra stringa e numero (il team_id dell'operatore e l'id della squadra possono
 * arrivare come tipi diversi). Ritorna `null` per valori assenti/vuoti/non
 * numerici, così da non far corrispondere id "vuoti" tra loro.
 *
 * @param {*} value
 * @returns {number|null}
 */
function normalizeId(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
}

/**
 * operatorHeader — intestazione leggibile per un gruppo autonomo sintetico:
 * usa `team_name` se presente, altrimenti `${cognome} ${nome}`.
 *
 * @param {Object} operator
 * @returns {string}
 */
function operatorHeader(operator) {
  if (operator && operator.team_name) return String(operator.team_name);
  const cognome = operator && operator.cognome ? String(operator.cognome) : "";
  const nome = operator && operator.nome ? String(operator.nome) : "";
  return `${cognome} ${nome}`.trim();
}

/**
 * makeTeamGroup — costruisce un gruppo per una squadra reale (da get-users).
 *
 * @param {Object} team
 * @returns {Object} gruppo squadra
 */
function makeTeamGroup(team) {
  return {
    team: {
      id: team.id,
      fullname: team.fullname,
      group: team.group,
      email: team.email,
      is_autonomo: !!team.is_autonomo
    },
    operators: [],
    isUnassigned: false,
    isAutonomous: false
  };
}

/**
 * groupOperatorsByTeam — combina operatori e squadre lato client producendo
 * l'array di gruppi ordinato per la Vista_Raggruppata.
 *
 * Regole (design "Algoritmo di costruzione"):
 *   - Un gruppo per OGNI squadra, anche vuota (operators: [], isUnassigned: false).
 *   - Match operatore↔squadra su team_id === team.id (confronto normalizzato).
 *   - team_id assente/non corrispondente:
 *       * dipendente (is_autonomo falsy)  → Gruppo_Senza_Squadra (team: null, isUnassigned: true)
 *       * autonomo   (is_autonomo truthy) → gruppo autonomo sintetico (team: null, isAutonomous: true)
 *   - Un gruppo squadra è isAutonomous: true se almeno un membro è autonomo.
 *   - Ogni operatore compare in un solo gruppo (unicità, R8.3).
 *   - Ordinamento: gruppi squadra reali + gruppi autonomi sintetici ordinati per
 *     intestazione (fullname/header) case-insensitive, localeCompare 'it';
 *     il Gruppo_Senza_Squadra è sempre accodato in fondo, e solo se non vuoto.
 *
 * @param {Array} operators
 * @param {Array} teams
 * @returns {Array} array di gruppi
 */
function groupOperatorsByTeam(operators, teams) {
  const safeOperators = Array.isArray(operators) ? operators : [];
  const safeTeams = Array.isArray(teams) ? teams : [];

  // Indicizza le squadre per id normalizzato, creando un gruppo per ognuna.
  const teamGroupsById = new Map();
  const teamGroups = [];
  for (const team of safeTeams) {
    const key = normalizeId(team.id);
    if (key === null) continue;
    if (teamGroupsById.has(key)) continue; // evita gruppi duplicati per id ripetuti
    const group = makeTeamGroup(team);
    teamGroupsById.set(key, group);
    teamGroups.push(group);
  }

  // Gruppo Senza squadra (creato lazy, incluso solo se popolato).
  const unassignedGroup = {
    team: null,
    operators: [],
    isUnassigned: true,
    isAutonomous: false
  };

  // Gruppi autonomi sintetici (uno per operatore autonomo senza team valido).
  const syntheticAutonomousGroups = [];

  for (const operator of safeOperators) {
    const teamKey = normalizeId(operator.team_id);
    const matchedGroup = teamKey === null ? undefined : teamGroupsById.get(teamKey);

    if (matchedGroup) {
      matchedGroup.operators.push(operator);
      if (operator.is_autonomo) matchedGroup.isAutonomous = true;
      continue;
    }

    // team_id assente o non corrispondente a nessuna squadra nota.
    if (operator.is_autonomo) {
      syntheticAutonomousGroups.push({
        team: null,
        operators: [operator],
        isUnassigned: false,
        isAutonomous: true,
        // intestazione derivata dall'operatore (usata per rendering/ordinamento)
        header: operatorHeader(operator)
      });
    } else {
      unassignedGroup.operators.push(operator);
    }
  }

  // Chiave di ordinamento: fullname per squadre reali, header per autonomi sintetici.
  const headerOf = (group) => {
    if (group.team && group.team.fullname) return String(group.team.fullname);
    if (group.header) return String(group.header);
    return "";
  };

  const orderedGroups = [...teamGroups, ...syntheticAutonomousGroups].sort((a, b) =>
    headerOf(a).localeCompare(headerOf(b), "it", { sensitivity: "base" })
  );

  if (unassignedGroup.operators.length > 0) {
    orderedGroups.push(unassignedGroup);
  }

  return orderedGroups;
}

/**
 * matchesSearch — verifica se un operatore corrisponde al termine di ricerca
 * su nome/cognome/qualifica (case-insensitive).
 *
 * @param {Object} operator
 * @param {string} term termine già in minuscolo
 * @returns {boolean}
 */
function matchesSearch(operator, term) {
  const nome = operator && operator.nome ? String(operator.nome).toLowerCase() : "";
  const cognome = operator && operator.cognome ? String(operator.cognome).toLowerCase() : "";
  const qualifica = operator && operator.qualifica ? String(operator.qualifica).toLowerCase() : "";
  return nome.includes(term) || cognome.includes(term) || qualifica.includes(term);
}

/**
 * filterGroups — applica filtro e ricerca sopra i gruppi, senza mutare l'input.
 *
 * Regole (design "Filtri e ricerca"):
 *   - teamFilter 'TEAMS' nasconde il Gruppo_Senza_Squadra; 'ALL' (o altro) lo include.
 *   - Ricerca testuale case-insensitive su nome/cognome/qualifica dei membri:
 *       * con search NON vuoto: filtra i membri e mostra un gruppo solo se ha
 *         almeno un membro corrispondente (le squadre vuote scompaiono);
 *       * con search vuoto: i gruppi restano com'erano (incluse le squadre vuote).
 *   - Non muta gli input: ritorna nuovi array/oggetti.
 *
 * @param {Array} groups
 * @param {{ search?: string, teamFilter?: string }} options
 * @returns {Array} nuovi gruppi filtrati
 */
function filterGroups(groups, options) {
  const safeGroups = Array.isArray(groups) ? groups : [];
  const opts = options || {};
  const rawSearch = typeof opts.search === "string" ? opts.search : "";
  const term = rawSearch.trim().toLowerCase();
  const teamFilter = opts.teamFilter;

  // 1) Filtro per tipo: TEAMS nasconde il Gruppo_Senza_Squadra.
  let working = safeGroups;
  if (teamFilter === "TEAMS") {
    working = working.filter((g) => !g.isUnassigned);
  }

  // 2) Ricerca testuale.
  if (term === "") {
    // Nessuna ricerca: ritorna copie dei gruppi (senza mutare l'input).
    return working.map((g) => ({
      ...g,
      operators: [...g.operators]
    }));
  }

  const result = [];
  for (const group of working) {
    const matched = group.operators.filter((op) => matchesSearch(op, term));
    if (matched.length > 0) {
      result.push({
        ...group,
        operators: matched
      });
    }
  }
  return result;
}

/**
 * isSquad — riconosce un utente come Squadra confrontando il suo `group` con i
 * `value` delle tipologie squadra note (coerente con la logica esistente, VN5):
 *   allTeamTypes.some(t => t.value === group)
 *
 * @param {string} group
 * @param {Array} allTeamTypes
 * @returns {boolean}
 */
function isSquad(group, allTeamTypes) {
  const types = Array.isArray(allTeamTypes) ? allTeamTypes : [];
  return types.some((t) => t.value === group);
}

/**
 * resolveTeamToEdit — individua l'utente-squadra da aprire in edit nel Backoffice
 * per il flusso "Modifica squadra".
 *
 * Confronta `editTeamId` con `user.id` normalizzando stringa/numero (il query
 * param arriva come stringa, l'id può essere numerico) e ritorna l'utente SOLO
 * se `isSquadFn(user.group)` è true. Ritorna `null` se editTeamId è assente/
 * vuoto/undefined, non trovato, o corrisponde a un utente non-squadra.
 *
 * @param {Array} users elenco utenti (da get-users)
 * @param {*} editTeamId identificativo squadra (tipicamente stringa da query param)
 * @param {function} isSquadFn predicato group -> boolean
 * @returns {Object|null}
 */
function resolveTeamToEdit(users, editTeamId, isSquadFn) {
  const safeUsers = Array.isArray(users) ? users : [];
  const targetId = normalizeId(editTeamId);
  if (targetId === null) return null;

  const match = safeUsers.find((u) => normalizeId(u.id) === targetId);
  if (!match) return null;

  const isSquadPredicate = typeof isSquadFn === "function" ? isSquadFn : () => false;
  if (!isSquadPredicate(match.group)) return null;

  return match;
}

module.exports = {
  groupOperatorsByTeam,
  filterGroups,
  isSquad,
  resolveTeamToEdit,
  // Esportate per riuso/test mirati della normalizzazione, se utile.
  normalizeId
};
